import { useEffect, useState } from "react"
import api from "../components/api/api"
import stringHelpers from "./stringHelpers"
import arrayUtilities from "./arrayUtilities"
import { HoverTooltip } from "../components/shared/hoverTooltip"
import Enums from "./enum"

export const useHealthCheckStatus = (organisationId, token) => {
    const [organisationSettings, setOrganisationSettings] = useState(null)
    const [sites, setSites] = useState()
    const [isLoading, setIsLoading] = useState(true)

    const [letterTemplates, setLetterTemplates] = useState(null)
    const [cancellationReasons, setCancellationReasons] = useState(null)
    const [voidReasons, setVoidReasons] = useState(null)
    const [referenceNumberRanges, setReferenceNumberRanges] = useState(null)
    const [clientsData, setClientsData] = useState(null)
    const [contraventions, setContraventions] = useState(null)
    const [siteContraventionsData, setSiteContraventionsData] = useState(null)
    const [siteVariableAnprRangesData, setSiteVariableAnprRangesData] = useState(null)

    const organisationSettingsUrl = `/organisation/${ organisationId }/organisation-settings`

    const kadoeSettingsUrl = `${ organisationSettingsUrl }#kadoe-settings`
    const pcnPortalSettingsUrl = `${ organisationSettingsUrl }#pcn-portal-settings`
    const paymentProviderSettingsUrl = `${ organisationSettingsUrl }#payment-provider-settings`
    const cashlessProvidersSettingsUrl = `${ organisationSettingsUrl }#cashless-providers-settings`
    const ipcBpaSettingsUrl = `${ organisationSettingsUrl }#ipc-bpa-settings`
    const poplaSettingsUrl = `${ organisationSettingsUrl }#popla-settings`
    const ipcDataExportSettingsUrl = `${ organisationSettingsUrl }#ipc-data-export-settings`

    const severityAccentColors = {
        danger: { accentColor: "#e63757", headerBackground: "#fce7eb" },
        warning: { accentColor: "#f5803e", headerBackground: "#fef0e8" },
    }
    const pileHairlineColor = "#edf2f9"

    const sitesPile = (sites, status = "danger", buildHref = site => `/organisation/${ organisationId }/client/${ site.clientId }/site/${ site.id }`) => {
        const colorClassName = `text-${ status }`
        const siteHref = buildHref
        const firstSite = <a className={ `font-weight-bold ${ colorClassName } text-decoration-underline` } target="_blank" href={ siteHref(sites[0]) }>{ sites[0].name }</a>

        if (sites.length === 1) {
            return firstSite
        } else if (sites.length > 1) {
            const remainingSites = sites.slice(1)
            const { accentColor, headerBackground } = severityAccentColors[status] ?? severityAccentColors.danger

            return <>
                { firstSite } and{ " " }
                <HoverTooltip
                    trigger={ <a type="button" className={ `font-weight-bold ${ colorClassName } text-decoration-underline` }><b>{ sites.length - 1 } more <i></i></b></a> }
                    placement="right"
                    maxHeight={ 220 }
                    header={ `Other affected sites · ${ remainingSites.length }` }
                    accentColor={ accentColor }
                    headerBackground={ headerBackground }
                >
                    <ul className="m-0 p-0 text-left">
                        {
                            remainingSites.map(
                                (site, index) => (
                                    <li key={ site.id } style={ { borderBottom: index < remainingSites.length - 1 ? `1px solid ${ pileHairlineColor }` : "none" } }>
                                        <a
                                            className="font-weight-bold d-inline-block text-truncate"
                                            style={ { maxWidth: "100%", verticalAlign: "bottom", display: "block", padding: "6px 4px" } }
                                            target="_blank"
                                            href={ siteHref(site) }
                                        >
                                            { site.name }
                                        </a>
                                    </li>
                                )
                            )
                        }
                    </ul>
                </HoverTooltip>
            </>
        }
    }

    const clientsPile = (clients) => {
        const clientHref = client => `/organisation/${ organisationId }/client/${ client.id }`
        const firstClient = <a className="font-weight-bold text-warning text-decoration-underline" target="_blank" href={ clientHref(clients[0]) }>{ clients[0].name }</a>

        if (clients.length === 1) {
            return firstClient
        } else if (clients.length > 1) {
            const remainingClients = clients.slice(1)
            const { accentColor, headerBackground } = severityAccentColors.warning

            return <>
                { firstClient } and{ " " }
                <HoverTooltip
                    trigger={ <a type="button" className="font-weight-bold text-warning text-decoration-underline"><b>{ clients.length - 1 } more <i></i></b></a> }
                    placement="right"
                    maxHeight={ 220 }
                    header={ `Other inactive clients · ${ remainingClients.length }` }
                    accentColor={ accentColor }
                    headerBackground={ headerBackground }
                >
                    <ul className="m-0 p-0 text-left">
                        {
                            remainingClients.map(
                                (client, index) => (
                                    <li key={ client.id } style={ { borderBottom: index < remainingClients.length - 1 ? `1px solid ${ pileHairlineColor }` : "none" } }>
                                        <a
                                            className="font-weight-bold d-inline-block text-truncate"
                                            style={ { maxWidth: "100%", verticalAlign: "bottom", display: "block", padding: "6px 4px" } }
                                            target="_blank"
                                            href={ clientHref(client) }
                                        >
                                            { client.name }
                                        </a>
                                    </li>
                                )
                            )
                        }
                    </ul>
                </HoverTooltip>
            </>
        }
    }

    const reasonsPile = (reasons, urlSegment) => {
        const reasonHref = reason => `/organisation/${ organisationId }/${ urlSegment }/${ reason.id }`
        const firstReason = <a className="font-weight-bold text-danger text-decoration-underline" target="_blank" href={ reasonHref(reasons[0]) }>{ reasons[0].name }</a>

        if (reasons.length === 1) {
            return firstReason
        } else if (reasons.length > 1) {
            const remainingReasons = reasons.slice(1)
            const { accentColor, headerBackground } = severityAccentColors.danger

            return <>
                { firstReason } and{ " " }
                <HoverTooltip
                    trigger={ <a type="button" className="font-weight-bold text-danger text-decoration-underline"><b>{ reasons.length - 1 } more <i></i></b></a> }
                    placement="right"
                    maxHeight={ 220 }
                    header={ `Other inactive reasons · ${ remainingReasons.length }` }
                    accentColor={ accentColor }
                    headerBackground={ headerBackground }
                >
                    <ul className="m-0 p-0 text-left">
                        {
                            remainingReasons.map(
                                (reason, index) => (
                                    <li key={ reason.id } style={ { borderBottom: index < remainingReasons.length - 1 ? `1px solid ${ pileHairlineColor }` : "none" } }>
                                        <a
                                            className="font-weight-bold d-inline-block text-truncate"
                                            style={ { maxWidth: "100%", verticalAlign: "bottom", display: "block", padding: "6px 4px" } }
                                            target="_blank"
                                            href={ reasonHref(reason) }
                                        >
                                            { reason.name }
                                        </a>
                                    </li>
                                )
                            )
                        }
                    </ul>
                </HoverTooltip>
            </>
        }
    }

    const templatesPile = (templates) => {
        const templateHref = template => `/organisation/${ organisationId }/document-template/${ template.id }`
        const firstTemplate = <a className="font-weight-bold text-warning text-decoration-underline" target="_blank" href={ templateHref(templates[0]) }>{ templates[0].name }</a>

        if (templates.length === 1) {
            return firstTemplate
        } else if (templates.length > 1) {
            const remainingTemplates = templates.slice(1)
            const { accentColor, headerBackground } = severityAccentColors.warning

            return <>
                { firstTemplate } and{ " " }
                <HoverTooltip
                    trigger={ <a type="button" className="font-weight-bold text-warning text-decoration-underline"><b>{ templates.length - 1 } more <i></i></b></a> }
                    placement="right"
                    maxHeight={ 220 }
                    header={ `Other inactive templates · ${ remainingTemplates.length }` }
                    accentColor={ accentColor }
                    headerBackground={ headerBackground }
                >
                    <ul className="m-0 p-0 text-left">
                        {
                            remainingTemplates.map(
                                (template, index) => (
                                    <li key={ template.id } style={ { borderBottom: index < remainingTemplates.length - 1 ? `1px solid ${ pileHairlineColor }` : "none" } }>
                                        <a
                                            className="font-weight-bold d-inline-block text-truncate"
                                            style={ { maxWidth: "100%", verticalAlign: "bottom", display: "block", padding: "6px 4px" } }
                                            target="_blank"
                                            href={ templateHref(template) }
                                        >
                                            { template.name }
                                        </a>
                                    </li>
                                )
                            )
                        }
                    </ul>
                </HoverTooltip>
            </>
        }
    }

    const contraventionsPile = (contraventions) => {
        const contraventionHref = contravention => `/organisation/${ organisationId }/contravention/${ contravention.id }`
        const firstContravention = <a className="font-weight-bold text-danger text-decoration-underline" target="_blank" href={ contraventionHref(contraventions[0]) }>{ contraventions[0].name }</a>

        if (contraventions.length === 1) {
            return firstContravention
        } else if (contraventions.length > 1) {
            const remainingContraventions = contraventions.slice(1)
            const { accentColor, headerBackground } = severityAccentColors.danger

            return <>
                { firstContravention } and{ " " }
                <HoverTooltip
                    trigger={ <a type="button" className="font-weight-bold text-danger text-decoration-underline"><b>{ contraventions.length - 1 } more <i></i></b></a> }
                    placement="right"
                    maxHeight={ 220 }
                    header={ `Other affected contraventions · ${ remainingContraventions.length }` }
                    accentColor={ accentColor }
                    headerBackground={ headerBackground }
                >
                    <ul className="m-0 p-0 text-left">
                        {
                            remainingContraventions.map(
                                (contravention, index) => (
                                    <li key={ contravention.id } style={ { borderBottom: index < remainingContraventions.length - 1 ? `1px solid ${ pileHairlineColor }` : "none" } }>
                                        <a
                                            className="font-weight-bold d-inline-block text-truncate"
                                            style={ { maxWidth: "100%", verticalAlign: "bottom", display: "block", padding: "6px 4px" } }
                                            target="_blank"
                                            href={ contraventionHref(contravention) }
                                        >
                                            { contravention.name }
                                        </a>
                                    </li>
                                )
                            )
                        }
                    </ul>
                </HoverTooltip>
            </>
        }
    }

    const fetchChecklistData = () => {
        setIsLoading(true)

        Promise.all([
            new Promise((resolve, reject) => api.getOrganisationSettings(resolve, reject, organisationId, token)),
            new Promise((resolve, reject) => api.getSites(resolve, reject, organisationId, token)),
            new Promise((resolve, reject) => api.getLetterTemplates(resolve, reject, organisationId, token)),
            new Promise((resolve, reject) => api.getNumberRanges(resolve, reject, organisationId, token)),
            new Promise((resolve, reject) => api.getContraventions(resolve, reject, organisationId, token)),
            new Promise((resolve, reject) => api.getCancellationReasons(resolve, reject, organisationId, token)),
            new Promise((resolve, reject) => api.getVoidReasons(resolve, reject, organisationId, token)),
            new Promise((resolve, reject) => api.getClients(resolve, reject, organisationId, token)),
            new Promise((resolve, reject) => api.getAllSiteVariableAnprRanges(resolve, reject, organisationId, token)),
            new Promise((resolve, reject) => api.getAllSiteContraventions(resolve, reject, organisationId, token)),
        ]).then(([organisationSettingsData, sitesData, letterTemplatesData, numberRangesData, contraventionsData, cancellationReasonsData, voidReasonsData, clientsData, siteVariableAnprRangesData, siteContraventionsData]) => {
            setOrganisationSettings(organisationSettingsData)
            setSites(sitesData)
            setLetterTemplates(letterTemplatesData)
            setReferenceNumberRanges(numberRangesData)
            setContraventions(contraventionsData)
            setCancellationReasons(cancellationReasonsData)
            setVoidReasons(voidReasonsData)
            setClientsData(clientsData)
            setSiteVariableAnprRangesData(siteVariableAnprRangesData)
            setSiteContraventionsData(siteContraventionsData)
        }).catch(error => {
            console.log(error)
        }).finally(() => {
            setIsLoading(false)
        })
    }

    useEffect(() => {
        if (!organisationId) {
            return
        }

        fetchChecklistData()
    }, [organisationId])

    const cashlessProviders = {
        "parkonomy": {
            displayName: "Parkonomy",
            checklistValidationKeys: ["parkonomyApiKey"],
            siteCodeKey: "parkonomySiteCode"
        },
        "ringGo": {
            displayName: "RingGo",
            checklistValidationKeys: ["ringGoApiPublicKey", "ringGoApiPrivateKey"],
            siteCodeKey: "ringGoZone"
        },
        "parkBunny": {
            displayName: "ParkBunny",
            checklistValidationKeys: ["parkBunnyApiKey"],
            siteCodeKey: "parkBunnyLocationId"
        },
        "justPark": {
            displayName: "JustPark",
            checklistValidationKeys: ["justParkApiKey"],
            siteCodeKey: "justParkLocationId"
        },
        "tap2Park": {
            displayName: "Tap2Park",
            checklistValidationKeys: ["tap2ParkApiKey"],
            siteCodeKey: "tap2ParkLocationId"
        },
        "payByPhone": {
            displayName: "PayByPhone",
            checklistValidationKeys: ["payByPhoneUsername", "payByPhonePassword"],
            siteCodeKey: "payByPhoneLocationId"
        },
        "ips": {
            displayName: "IPS",
            checklistValidationKeys: ["ipsApiKey"],
            siteCodeKey: "ipsLocationId"
        },
        "convious": {
            displayName: "Convious",
            checklistValidationKeys: ["conviousApiKey"],
            siteCodeKey: "conviousLocationId"
        },
        "lookingForCarPark": {
            displayName: "Looking For Car Park",
            checklistValidationKeys: ["lookingForCarParkApiKey"],
            siteCodeKey: "lookingForCarParkLocationId"
        },
        "newPark": {
            displayName: "NewPark",
            checklistValidationKeys: ["newParkApiKey"],
            siteCodeKey: "newParkLocationId"
        },
        "parkso": {
            displayName: "Parkso",
            checklistValidationKeys: ["parksoApiKey"],
            siteCodeKey: "parksoLocationId"
        },
        "myCarPass": {
            displayName: "My Car Pass",
            checklistValidationKeys: ["myCarPassApiKey"],
            siteCodeKey: "myCarPassLocationId"
        },
    }

    const areCashlessProvidersSetup = () => {
        const sitesUrl = `/organisation/${ organisationId }/sites`
        const actions = [
            { label: "Review cashless providers", url: cashlessProvidersSettingsUrl },
            { label: "Review sites", url: sitesUrl }
        ]

        const isAnyProviderConfigured = Object.values(cashlessProviders).some(
            provider => provider.checklistValidationKeys.every(key => !stringHelpers.isNullOrEmpty(organisationSettings[key]))
        )

        if (arrayUtilities.isNullOrEmpty(sites)) {
            return isAnyProviderConfigured
                ? {
                    status: "success",
                    statusDetails: ["At least one cashless provider is configured at the organisation level."],
                    actions: []
                }
                : {
                    status: "warning",
                    statusDetails: ["No cashless providers are configured yet, and there are no sites to check them against."],
                    actions: [{ label: "Review cashless providers", url: cashlessProvidersSettingsUrl }]
                }
        }

        const mismatchedProviderDetails = Object.values(cashlessProviders).reduce((details, provider) => {
            const isProviderConfigured = provider.checklistValidationKeys.every(
                key => !stringHelpers.isNullOrEmpty(organisationSettings[key])
            )
            const sitesUsingProvider = sites.filter(
                site => !stringHelpers.isNullOrEmpty(site[provider.siteCodeKey])
            )

            if (!isProviderConfigured && sitesUsingProvider.length > 0) {
                details.push(
                    <span>{ provider.displayName } has no API key configured but is referenced by { sitesPile(sitesUsingProvider) }</span>
                )
            }

            return details
        }, [])

        if (!arrayUtilities.isNullOrEmpty(mismatchedProviderDetails)) {
            return { status: "danger", statusDetails: mismatchedProviderDetails, actions }
        }

        if (isAnyProviderConfigured) {
            return {
                status: "success",
                statusDetails: ["Every cashless provider referenced by a site has a matching API key configured."],
                actions: []
            }
        }

        return {
            status: "warning",
            statusDetails: ["No cashless providers are configured or referenced by any site yet."],
            actions: [{ label: "Review cashless providers", url: cashlessProvidersSettingsUrl }]
        }
    }

    const areCancellationAndVoidReasonsSetup = (cancellationReasons, voidReasons) => {
        if (arrayUtilities.isNullOrEmpty([...cancellationReasons, ...voidReasons])) {
            return {
                status: "warning",
                statusDetails: ["No cancellation and void reasons set up."],
                actions: [{ label: "Review reasons", url: `/organisation/${ organisationId }/cancellation-reasons` }]
            }
        }
        if ([...cancellationReasons, ...voidReasons].every(reason => reason.isActive)) {
            return {
                status: "success",
                statusDetails: [],
                actions: []
            }
        }
        const inactiveCancellationReasons = cancellationReasons.filter(reason => !reason.isActive)
        const inactiveVoidReasons = voidReasons.filter(reason => !reason.isActive)
        let details = ["Any existing cancellation or void reasons must be activated."]
        let result = {
            status: "danger",
            statusDetails: [],
            actions: [{ label: "Review reasons", url: `/organisation/${ organisationId }/cancellation-reasons` }]
        }

        if (!arrayUtilities.isNullOrEmpty(inactiveCancellationReasons)) {
            const count = inactiveCancellationReasons.length

            details.push(
                <span>
                    There { count === 1 ? "is" : "are" } { count } inactive { stringHelpers.pluralSuffixModifier(count, "cancellation reasons") }: { reasonsPile(inactiveCancellationReasons, "cancellation-reason") }
                </span>
            )
        }

        if (!arrayUtilities.isNullOrEmpty(inactiveVoidReasons)) {
            const count = inactiveVoidReasons.length

            details.push(
                <span>
                    There { count === 1 ? "is" : "are" } { count } inactive { stringHelpers.pluralSuffixModifier(count, "void reasons") }: { reasonsPile(inactiveVoidReasons, "void-reason") }
                </span>
            )
        }

        return {
            ...result,
            statusDetails: details
        }

    }

    const areReferenceNumberRangesSetup = (ranges, contraventionsToCheck) => {
        const referenceNumberRangesUrl = `/organisation/${ organisationId }/reference-number-ranges`
        const contraventionSettingsUrl = `/organisation/${ organisationId }/contravention-settings`

        const anyAnprContraventionsActive = contraventionsToCheck.some(contravention => contravention.isActive && contravention.contraventionType === 0)
        const activeAnprContraventions = contraventionsToCheck.filter(contravention => contravention.isActive && contravention.contraventionType === 0).length

        const anyMnprContraventionsActive = contraventionsToCheck.some(contravention => contravention.isActive && contravention.contraventionType === 1)
        const activeMnprContraventions = contraventionsToCheck.filter(contravention => contravention.isActive && contravention.contraventionType === 1).length

        const anyAnprRangesActive = ranges.some(range => range.contraventionType === 0 && range.isActive)
        const activeAnprRanges = ranges.filter(range => range.contraventionType === 0 && range.isActive).length

        const anyMnprRangesActive = ranges.some(range => range.contraventionType === 1 && range.isActive)
        const activeMnprRanges = ranges.filter(range => range.contraventionType === 1 && range.isActive).length

        // checks for bidirectional mismatch
        const isAnprMisconfigured = (anyAnprContraventionsActive && !anyAnprRangesActive) || (!anyAnprContraventionsActive && anyAnprRangesActive)
        const isMnprMisconfigured = (anyMnprContraventionsActive && !anyMnprRangesActive) || (!anyMnprContraventionsActive && anyMnprRangesActive)

        const anprDetail = anyAnprContraventionsActive
            ? `There are ${ activeAnprContraventions } active ANPR contravention(s) but no active ANPR reference number ranges.`
            : `There are ${ activeAnprRanges } active ANPR reference number range(s) but no active ANPR contraventions.`
        const actions = [
            { label: "Review reference number ranges", url: referenceNumberRangesUrl },
            { label: "Review contraventions", url: contraventionSettingsUrl }
        ]

        const mnprDetail = anyMnprContraventionsActive
            ? `There are ${ activeMnprContraventions } active MNPR contravention(s) but no active MNPR reference number ranges.`
            : `There are ${ activeMnprRanges } active MNPR reference number range(s) but no active MNPR contraventions.`

        const noActiveRangesDetails = []
        if (activeAnprRanges === 0) {
            noActiveRangesDetails.push("No active ANPR ranges set up")
        }
        if (activeMnprRanges === 0) {
            noActiveRangesDetails.push("No active MNPR ranges set up")
        }

        if (!arrayUtilities.isNullOrEmpty(noActiveRangesDetails)) {
            return {
                status: "danger",
                statusDetails: noActiveRangesDetails,
                actions: actions
            }
        }

        if (isAnprMisconfigured && isMnprMisconfigured) {
            return {
                status: "danger",
                statusDetails: [anprDetail, mnprDetail],
                actions: actions
            }
        }

        if (isAnprMisconfigured) {
            return {
                status: "danger",
                statusDetails: [anprDetail],
                actions: actions
            }
        }

        if (isMnprMisconfigured) {
            return {
                status: "danger",
                statusDetails: [mnprDetail],
                actions: actions
            }
        }

        if ((anyAnprContraventionsActive && anyAnprRangesActive) || (anyMnprContraventionsActive && anyMnprRangesActive)) {
            return {
                status: "success",
                statusDetails: ["Every active contravention type currently has active reference number ranges configured for it."],
                actions: []
            }
        }

        return {
            status: "warning",
            statusDetails: ["No active ANPR or MNPR contraventions."],
            actions: actions
        }
    }

    const areClientsSetup = (clients) => {
        const actions = [
            { label: "Review clients", url: `/organisation/${ organisationId }/clients` }
        ]

        if (!arrayUtilities.isNullOrEmpty(clients) && clients.every(client => client.isActive)) {
            return {
                status: "success",
                statusDetails: [],
                actions: []
            }
        }
        if (arrayUtilities.isNullOrEmpty(clients)) {
            return {
                status: "warning",
                statusDetails: ["No clients set up yet."],
                actions: actions
            }
        }

        const inactiveClients = clients.filter(client => !client.isActive)
        const count = inactiveClients.length
        return {
            status: "warning",
            statusDetails: [
                <span>There { count === 1 ? "is" : "are" } { count } inactive { stringHelpers.pluralSuffixModifier(count, "clients") }: { clientsPile(inactiveClients) }</span>
            ],
            actions: actions
        }
    }

    const areSitesSetup = (sitesToCheck) => {
        const actions = [
            { label: "Review sites", url: `/organisation/${ organisationId }/sites` }
        ]

        if (arrayUtilities.isNullOrEmpty(sitesToCheck)) {
            return {
                status: "warning",
                statusDetails: ["No sites set up yet."],
                actions: actions
            }
        }

        if (sitesToCheck.every(site => site.isActive)) {
            return {
                status: "success",
                statusDetails: [],
                actions: []
            }
        }

        const inactiveSites = sitesToCheck.filter(site => !site.isActive)
        const count = inactiveSites.length

        return {
            status: "warning",
            statusDetails: [
                <span>There { count === 1 ? "is" : "are" } { count } inactive { stringHelpers.pluralSuffixModifier(count, "sites") }: { sitesPile(inactiveSites, "warning") }</span>
            ],
            actions: actions
        }
    }

    const isIpcBpaInSitesSetup = (sitesToCheck, settings) => {
        const actions = [
            { label: "Review sites", url: `/organisation/${ organisationId }/sites` },
            { label: "Review IPC settings", url: ipcBpaSettingsUrl },
            { label: "Review POPLA prefixes", url: poplaSettingsUrl },
            { label: "Review IPC Data Export setup", url: ipcDataExportSettingsUrl }
        ]

        const activeSites = sitesToCheck.filter(site => site.isActive)

        if (arrayUtilities.isNullOrEmpty(activeSites)) {
            return {
                status: "warning",
                statusDetails: ["No active sites to check yet."],
                actions: actions
            }
        }

        const isIpcAccredited = !stringHelpers.isNullOrEmpty(settings.iasPublicHash) && !stringHelpers.isNullOrEmpty(settings.iasPrivateHash)

        const isBpaAccredited = !stringHelpers.isNullOrEmpty(settings.poplaCodePrefixEnglandAndWales)
            || !stringHelpers.isNullOrEmpty(settings.poplaCodePrefixScotland)
            || !stringHelpers.isNullOrEmpty(settings.poplaCodePrefixNorthernIreland)

        if (!isIpcAccredited && !isBpaAccredited) {
            return {
                status: "danger",
                statusDetails: ["This organisation needs one of IPC or BPA accreditation."],
                actions: actions
            }
        }

        const accreditationLabel = isIpcAccredited ? "IPC" : "BPA"
        const expectedEnabledField = isIpcAccredited ? "iasEnabled" : "bpaEnabled"
        const expectedEnabledLabel = isIpcAccredited ? "IAS" : "BPA"
        const expectedDisabledField = isIpcAccredited ? "bpaEnabled" : "iasEnabled"
        const expectedDisabledLabel = isIpcAccredited ? "BPA" : "IAS"

        const sitesMissingExpectedStatus = activeSites.filter(site => !site[expectedEnabledField])
        const sitesWithUnexpectedStatus = activeSites.filter(site => site[expectedDisabledField])

        if (arrayUtilities.isNullOrEmpty(sitesMissingExpectedStatus) && arrayUtilities.isNullOrEmpty(sitesWithUnexpectedStatus)) {
            return {
                status: "success",
                statusDetails: [],
                actions: []
            }
        }

        const missingExpectedStatusDetail = !arrayUtilities.isNullOrEmpty(sitesMissingExpectedStatus)
            ? <span>{ sitesMissingExpectedStatus.length } active { stringHelpers.pluralSuffixModifier(sitesMissingExpectedStatus.length, "sites") } { sitesMissingExpectedStatus.length === 1 ? "has" : "have" } { expectedEnabledLabel } Status disabled: { sitesPile(sitesMissingExpectedStatus, "danger") }</span>
            : null

        const unexpectedStatusDetail = !arrayUtilities.isNullOrEmpty(sitesWithUnexpectedStatus)
            ? <span>{ sitesWithUnexpectedStatus.length } active { stringHelpers.pluralSuffixModifier(sitesWithUnexpectedStatus.length, "sites") } { sitesWithUnexpectedStatus.length === 1 ? "has" : "have" } { expectedDisabledLabel } Status enabled: { sitesPile(sitesWithUnexpectedStatus, "danger") }</span>
            : null

        return {
            status: "danger",
            statusDetails: [
                `This organisation is ${ accreditationLabel } accredited`,
                missingExpectedStatusDetail,
                unexpectedStatusDetail,
            ],
            actions: actions
        }
    }

    const isPofaSetup = (sitesToCheck) => {
        const actions = [
            { label: "Review sites", url: `/organisation/${ organisationId }/sites` }
        ]

        const activeEnglandSites = sitesToCheck.filter(site => site.isActive && site.jurisdiction === 0)

        if (arrayUtilities.isNullOrEmpty(activeEnglandSites)) {
            return null
        }

        if (activeEnglandSites.every(site => site.pofaEnabled)) {
            return {
                status: "success",
                statusDetails: [],
                actions: []
            }
        }

        const misconfiguredSites = activeEnglandSites.filter(site => !site.pofaEnabled)
        const misconfiguredCount = misconfiguredSites.length
        const activeSitesCount = activeEnglandSites.length

        return {
            status: "warning",
            statusDetails: [
                <span>{ misconfiguredCount } of { activeSitesCount } active England &amp; Wales { stringHelpers.pluralSuffixModifier(activeSitesCount, "sites") } { misconfiguredCount === 1 ? "does" : "do" } not have PoFA enabled: { sitesPile(misconfiguredSites, "warning") }</span>
            ],
            actions: actions
        }
    }

    const areLetterTemplatesSetup = (templates) => {
        const actions = [
            { label: "Review letter templates", url: `/organisation/${ organisationId }/letter-templates` }
        ]

        if (arrayUtilities.isNullOrEmpty(templates)) {
            return {
                status: "warning",
                statusDetails: ["No letter templates set up yet."],
                actions: actions
            }
        }

        if (templates.every(template => template.isActive)) {
            return {
                status: "success",
                statusDetails: [],
                actions: []
            }
        }

        const inactiveTemplates = templates.filter(template => !template.isActive)
        const count = inactiveTemplates.length

        return {
            status: "warning",
            statusDetails: [
                <span>
                    There { count === 1 ? "is" : "are" } { count } inactive letter { stringHelpers.pluralSuffixModifier(count, "templates") }: { templatesPile(inactiveTemplates) }
                </span>
            ],
            actions: actions
        }
    }

    const areContraventionsSetup = (contraventionsToCheck) => {
        const actions = [
            { label: "Review contraventions", url: `/organisation/${ organisationId }/contravention-settings` }
        ]

        const activeContraventions = contraventionsToCheck.filter(contravention => contravention.isActive)

        if (arrayUtilities.isNullOrEmpty(activeContraventions)) {
            return {
                status: "warning",
                statusDetails: ["No active contraventions set up yet."],
                actions: actions
            }
        }

        const isContraventionConfigured = contravention =>
            !stringHelpers.isNullOrEmpty(String(contravention.reducedChargeAmount))
            && !stringHelpers.isNullOrEmpty(String(contravention.reducedChargeDays))
            && !stringHelpers.isNullOrEmpty(String(contravention.fullChargeAmount))
            && !stringHelpers.isNullOrEmpty(String(contravention.fullChargeDays))

        if (activeContraventions.every(isContraventionConfigured)) {
            return {
                status: "success",
                statusDetails: [],
                actions: []
            }
        }

        const misconfiguredContraventions = activeContraventions.filter(contravention => !isContraventionConfigured(contravention))
        const count = misconfiguredContraventions.length

        return {
            status: "danger",
            statusDetails: [
                <span>{ count } active { stringHelpers.pluralSuffixModifier(count, "contraventions") } { count === 1 ? "is" : "are" } missing charge amounts or day windows: { contraventionsPile(misconfiguredContraventions) }</span>
            ],
            actions: actions
        }
    }

    const isIpcOrBpaAccreditationSetup = (settings) => {
        const actions = [
            { label: "Review IPC setup", url: ipcBpaSettingsUrl },
            { label: "Review POPLA prefixes", url: poplaSettingsUrl },
            { label: "Review IPC Data Export setup", url: ipcDataExportSettingsUrl }
        ]

        const isIpcAccredited = !stringHelpers.isNullOrEmpty(settings.iasPublicHash) && !stringHelpers.isNullOrEmpty(settings.iasPrivateHash)

        if (isIpcAccredited) {
            const missingFieldDetails = [
                !settings.ipcDataExportEnabled ? "IPC data export is not enabled." : null,
                stringHelpers.isNullOrEmpty(settings.ipcDataExportHostName) ? "No IPC data export host name has been entered." : null,
                stringHelpers.isNullOrEmpty(settings.ipcDataExportUsername) ? "No IPC data export username has been entered." : null,
                stringHelpers.isNullOrEmpty(settings.ipcDataExportPassword) ? "No IPC data export password has been entered." : null,
                stringHelpers.isNullOrEmpty(settings.ipcDataExportBucket) ? "No IPC data export bucket has been entered." : null,
                stringHelpers.isNullOrEmpty(settings.ipcDataExportEncryptionKey) ? "No IPC data export encryption key has been entered." : null,
                stringHelpers.isNullOrEmpty(settings.ipcDataExportStaticIv) ? "No IPC data export static IV has been entered." : null
            ].filter(detail => detail !== null)

            if (arrayUtilities.isNullOrEmpty(missingFieldDetails)) {
                return {
                    status: "success",
                    statusDetails: ["This organisation is IPC accredited, and its IPC data export is fully configured."],
                    actions: []
                }
            }

            return {
                status: "danger",
                statusDetails: [
                    "This organisation is IPC accredited, which requires the IPC data export to be fully configured.",
                    ...missingFieldDetails
                ],
                actions: actions
            }
        }

        const isBpaAccredited = !stringHelpers.isNullOrEmpty(settings.poplaCodePrefixEnglandAndWales)
            || !stringHelpers.isNullOrEmpty(settings.poplaCodePrefixScotland)
            || !stringHelpers.isNullOrEmpty(settings.poplaCodePrefixNorthernIreland)

        if (isBpaAccredited) {
            return {
                status: "success",
                statusDetails: ["This organisation is BPA accredited, and has at least one POPLA code prefix configured."],
                actions: []
            }
        }
        if (!isBpaAccredited) {
            return {
                status: "danger",
                statusDetails: ["This organisation needs one of IPC or BPA accreditation."],
                actions: actions
            }
        }
    }

    const standardAnprContraventionIdFields = [
        "overstayContraventionId", "notOnAllowListContraventionId", "shortReturnContraventionId",
        "overnightStayContraventionId", "failedToMakePaymentContraventionId",
        "overstayedPaymentPeriodContraventionId"
    ]
    const variableAnprContraventionIdFields = [
        "overstayContraventionId", "shortReturnContraventionId", "failedToMakePaymentContraventionId",
        "overstayedPaymentPeriodContraventionId"
    ]

    const areSiteAnprContraventionsSetup = (sitesToCheck, siteContraventionsToCheck, siteVariableAnprRangesToCheck) => {
        const actions = [
            { label: "Review sites", url: `/organisation/${ organisationId }/sites` }
        ]

        // only active sites and their active site-contraventions are relevant -
        // an inactive site-contravention doesn't need anything wired up to it to be considered "fine"
        const activeSites = sitesToCheck.filter(site => site.isActive)
        if (arrayUtilities.isNullOrEmpty(activeSites)) {
            // nothing active to check yet - not a failure, just nothing configured
            return {
                status: "warning",
                statusDetails: ["No active sites to check yet."],
                actions: actions
            }
        }

        const activeSiteContraventions = siteContraventionsToCheck.filter(siteContravention => siteContravention.isActive)

        // every contravention actually referenced by an active site's ANPR settings - either directly
        // on the site (standard) or via one of its variable ANPR ranges
        const anprContraventionReferences = activeSites.reduce((references, site) => {
            standardAnprContraventionIdFields.forEach(field => {
                if (site[field] != null) {
                    references.push({ site, contraventionId: site[field] })
                }
            })

            siteVariableAnprRangesToCheck
                .filter(range => range.siteId === site.id)
                .forEach(range => {
                    variableAnprContraventionIdFields.forEach(field => {
                        if (range[field] != null) {
                            references.push({ site, contraventionId: range[field] })
                        }
                    })
                })

            return references
        }, [])

        // nothing configured in ANPR settings yet - not a failure, just nothing to check
        if (arrayUtilities.isNullOrEmpty(anprContraventionReferences)) {
            return {
                status: "warning",
                statusDetails: ["No contraventions applied to ANPR settings on any sites."],
                actions: actions
            }
        }

        // every contravention referenced by ANPR settings must actually be enabled (an active
        // site-contravention) for that same site, otherwise ANPR could raise a breach for a
        // contravention that isn't switched on
        const unwiredReferences = anprContraventionReferences.filter(reference => {
            const isReferenceWiredToAnActiveSiteContravention = activeSiteContraventions.some(
                siteContravention => siteContravention.siteId === reference.site.id && siteContravention.contraventionId === reference.contraventionId
            )

            return isReferenceWiredToAnActiveSiteContravention === false
        })

        if (arrayUtilities.isNullOrEmpty(unwiredReferences)) {
            return {
                status: "success",
                statusDetails: [],
                actions: []
            }
        }

        // group by contravention so each detail line names the contravention once and piles the
        // affected sites, rather than one bullet per individual site+contravention pair
        const unwiredSitesByContraventionId = unwiredReferences.reduce((groups, reference) => {
            const sitesForContravention = groups[reference.contraventionId] ?? []

            // the same site can reference the same contravention via both a standard field and a
            // variable ANPR range - only list it once
            if (!sitesForContravention.some(site => site.id === reference.site.id)) {
                sitesForContravention.push(reference.site)
            }

            groups[reference.contraventionId] = sitesForContravention
            return groups
        }, {})

        const statusDetails = Object.entries(unwiredSitesByContraventionId).map(([contraventionId, sitesForContravention]) => {
            const contravention = contraventions.find(c => c.id === contraventionId)
            const count = sitesForContravention.length
            const contraventionHref = site => `/organisation/${ organisationId }/site/${ site.id }/contravention/${ contraventionId }`

            return (
                <span>
                    <b>{ contravention?.name ?? "A contravention" }</b> is applied on ANPR settings but isn't an active contravention at { count } { stringHelpers.pluralSuffixModifier(count, "sites") }: { sitesPile(sitesForContravention, "danger", contraventionHref) }
                </span>
            )
        })

        return {
            status: "danger",
            statusDetails,
            actions
        }
    }

    const isKadoeSetup = (organisationSettings) => {
        let checklistItemResult = {
            status: "warning",
            statusDetails: "No Kadoe Connection Type set.",
            actions: [
                {
                    label: "Add Kadoe connection",
                    url: kadoeSettingsUrl
                }
            ]
        }
        switch (organisationSettings.kadoeConnectionType) {
            case 1:
                const result = !stringHelpers.isNullOrEmpty(organisationSettings.kadoeUsername) && !stringHelpers.isNullOrEmpty(organisationSettings.kadoePassword) && !stringHelpers.isNullOrEmpty(organisationSettings.kadoeApiKey) && !stringHelpers.isNullOrEmpty(organisationSettings.kadoeEnquirerId) ? "success" : "danger"

                checklistItemResult = {
                    ...checklistItemResult,
                    status: result,
                    statusDetails: [
                        "Connection type is set to Direct Link, which requires a username, password, API key, and Enquirer ID.",
                        stringHelpers.isNullOrEmpty(organisationSettings.kadoeUsername) ? "No Kadoe username has been entered." : null,
                        stringHelpers.isNullOrEmpty(organisationSettings.kadoePassword) ? "No Kadoe password has been entered." : null,
                        stringHelpers.isNullOrEmpty(organisationSettings.kadoeApiKey) ? "No Kadoe API key has been entered." : null,
                        stringHelpers.isNullOrEmpty(organisationSettings.kadoeEnquirerId) ? "No Enquirer ID has been entered" : null
                    ]
                }
                break;
            case 2:
                checklistItemResult = {
                    ...checklistItemResult,
                    status: !stringHelpers.isNullOrEmpty(organisationSettings.kadoeEnquirerId) ? "success" : "danger",
                    statusDetails: [
                        "Connection type is set to Link Provider, which requires an Enquirer ID.",
                        stringHelpers.isNullOrEmpty(organisationSettings.kadoeEnquirerId) ? "No Enquirer ID has been entered" : null
                    ]
                }
                break;
            case 3:
                checklistItemResult = {
                    ...checklistItemResult,
                    status: !stringHelpers.isNullOrEmpty(organisationSettings.kadoeEnquirerId) ? "success" : "danger",
                    statusDetails: [
                        "Connection type is set to Link Provider (Enhanced), which requires an Enquirer ID.",
                        stringHelpers.isNullOrEmpty(organisationSettings.kadoeEnquirerId) ? "No Enquirer ID has been entered" : null
                    ]
                }
                break;
            default:
                checklistItemResult = {
                    ...checklistItemResult,
                    status: "warning",
                    statusDetails: ["No Kadoe Connection Type set."]
                }
                break;
        }

        return checklistItemResult
    }

    const isPcnPortalSetup = (organisationSettings) => {
        const isConfigured = !stringHelpers.isNullOrEmpty(organisationSettings.managePcnHostName)

        return {
            status: isConfigured ? "success" : "danger",
            statusDetails: isConfigured
                ? ["A PCN Portal host name is configured."]
                : ["No PCN Portal host name configured."],
            actions: isConfigured ? [] : [{ label: "Review PCN Portal setup", url: pcnPortalSettingsUrl }]
        }
    }

    const isPaymentProviderSetup = (organisationSettings) => {
        const reviewPaymentProviderAction = {
            label: "Review payment provider setup",
            url: paymentProviderSettingsUrl
        }

        if (organisationSettings.paymentProvider === null) {
            return {
                status: "danger",
                statusDetails: ["No payment provider has been selected."],
                actions: [reviewPaymentProviderAction]
            }
        }

        if (organisationSettings.paymentProvider === 0) {
            const missingFieldDetails = [
                stringHelpers.isNullOrEmpty(organisationSettings.stripePublishableKey) ? "No Stripe publishable key has been entered." : null,
                stringHelpers.isNullOrEmpty(organisationSettings.stripeSecretKey) ? "No Stripe secret key has been entered." : null,
                stringHelpers.isNullOrEmpty(organisationSettings.stripeWebhookSecret) ? "No Stripe webhook secret has been entered." : null
            ].filter(detail => detail !== null)

            if (arrayUtilities.isNullOrEmpty(missingFieldDetails)) {
                return {
                    status: "success",
                    statusDetails: ["Stripe is selected and fully configured."],
                    actions: []
                }
            }

            return {
                status: "danger",
                statusDetails: ["Stripe is selected, which requires a publishable key, secret key, and webhook secret.", ...missingFieldDetails],
                actions: [reviewPaymentProviderAction]
            }
        }

        return {
            status: "success",
            statusDetails: [`${ Enums.PaymentProvider[organisationSettings.paymentProvider] } is selected as the payment provider.`],
            actions: []
        }
    }

    const kadoeSetupResult = !isLoading && organisationSettings ? isKadoeSetup(organisationSettings) : null
    const pcnPortalResult = !isLoading && organisationSettings ? isPcnPortalSetup(organisationSettings) : null
    const paymentProviderResult = !isLoading && organisationSettings ? isPaymentProviderSetup(organisationSettings) : null

    const cashlessProvidersResult = !isLoading && organisationSettings ? areCashlessProvidersSetup() : null
    const cancellationAndVoidReasonsResult = !isLoading && cancellationReasons && voidReasons ? areCancellationAndVoidReasonsSetup(cancellationReasons, voidReasons) : null
    const referenceNumberRangesResult = !isLoading && referenceNumberRanges && contraventions ? areReferenceNumberRangesSetup(referenceNumberRanges, contraventions) : null
    const clientsResult = !isLoading && clientsData ? areClientsSetup(clientsData) : null
    const sitesSetupResult = !isLoading && sites ? areSitesSetup(sites) : null
    const ipcOrBpaAccreditationResult = !isLoading && organisationSettings ? isIpcOrBpaAccreditationSetup(organisationSettings) : null
    const ipcBpaInSitesResult = !isLoading && sites && organisationSettings ? isIpcBpaInSitesSetup(sites, organisationSettings) : null
    const pofaResult = !isLoading && sites ? isPofaSetup(sites) : null
    const letterTemplatesResult = !isLoading && letterTemplates ? areLetterTemplatesSetup(letterTemplates) : null
    const contraventionsResult = !isLoading && contraventions ? areContraventionsSetup(contraventions) : null
    const siteAnprContraventionsResult = !isLoading && sites && siteContraventionsData && siteVariableAnprRangesData
        ? areSiteAnprContraventionsSetup(sites, siteContraventionsData, siteVariableAnprRangesData)
        : null

    const allChecklistStatuses = [
        kadoeSetupResult, pcnPortalResult, paymentProviderResult,
        cashlessProvidersResult, cancellationAndVoidReasonsResult, referenceNumberRangesResult,
        clientsResult, sitesSetupResult, ipcBpaInSitesResult, pofaResult, letterTemplatesResult, contraventionsResult,
        ipcOrBpaAccreditationResult, siteAnprContraventionsResult
    ].map(result => result?.status ?? null)

    const overallStatus = isLoading
        ? null
        : allChecklistStatuses.some(status => status === "danger")
            ? "danger"
            : allChecklistStatuses.some(status => status === "warning")
                ? "warning"
                : "success"

    const successCount = allChecklistStatuses.filter(status => status === "success").length
    const warningCount = allChecklistStatuses.filter(status => status === "warning").length
    const dangerCount = allChecklistStatuses.filter(status => status === "danger").length

    return {
        isLoading,
        overallStatus,
        successCount,
        warningCount,
        dangerCount,
        allChecklistStatuses,
        results: {
            kadoeSetupResult,
            pcnPortalResult,
            paymentProviderResult,
            cashlessProvidersResult,
            cancellationAndVoidReasonsResult,
            referenceNumberRangesResult,
            clientsResult,
            sitesSetupResult,
            ipcOrBpaAccreditationResult,
            ipcBpaInSitesResult,
            pofaResult,
            letterTemplatesResult,
            contraventionsResult,
            siteAnprContraventionsResult
        },
        refresh: fetchChecklistData
    }
}
